import { useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { gqlQuery } from 'app/lib/graphqlClient'
import { AttributionDayType, AttributionRowType, AttributionSummaryType } from 'app/types/attribution'

const ATTRIBUTION_METRICS_QUERY = graphql(`
  query AttributionMetrics(
    $dateRange: DateRangeInput!
    $groupBy: String!
    $locationIds: [String!]
    $integrationStatus: String
    $filters: String
    $showDisqualified: Boolean
  ) {
    marketingMetrics(
      dateRange: $dateRange
      groupBy: $groupBy
      locationIds: $locationIds
      integrationStatus: $integrationStatus
      filters: $filters
      showDisqualified: $showDisqualified
    ) {
      hits {
        bucket
        leads
        mqls
        appointments
        patients
        production
        scheduledProduction
        googleSpend
        facebookSpend
        roi
        budget
        byChannel {
          channel
        }
      }
      summary {
        leads
        mqls
        appointments
        patients
        production
        scheduledProduction
      }
    }
  }
`)

// The Ad Campaign grouping returns trackableCampaignId buckets; the table shows
// the campaign name and filters by the id (TRK-3949).
const ATTRIBUTION_AD_CAMPAIGNS_QUERY = graphql(`
  query AttributionAdCampaigns($search: String, $pagination: PaginationInput) {
    campaigns(search: $search, pagination: $pagination) {
      hits {
        id
        name
      }
    }
  }
`)

const ATTRIBUTION_DAYS_QUERY = graphql(`
  query AttributionDays(
    $dateRange: DateRangeInput!
    $locationIds: [String!]
    $integrationStatus: String
    $filters: String
    $showDisqualified: Boolean
  ) {
    marketingMetrics(
      dateRange: $dateRange
      groupBy: "days"
      locationIds: $locationIds
      integrationStatus: $integrationStatus
      filters: $filters
      showDisqualified: $showDisqualified
    ) {
      hits {
        bucket
        leads
        mqls
        appointments
        patients
        byChannel {
          channel
          leads
          mqls
          appointments
          patients
        }
      }
    }
  }
`)

export type GqlRow = {
  bucket: string
  leads: number
  mqls: number
  appointments: number
  patients: number
  production?: number
  scheduledProduction?: number
  googleSpend?: number
  facebookSpend?: number
  roi?: number
  budget?: number
  byChannel?: { channel: string; leads?: number; mqls?: number; appointments?: number; patients?: number }[]
}

type GqlResponse = {
  marketingMetrics: {
    hits: GqlRow[]
    summary?: {
      leads: number
      mqls: number
      appointments: number
      patients: number
      production: number
      scheduledProduction: number
    }
  }
}

export function toAttributionRow(row: GqlRow): AttributionRowType {
  return {
    bucket: row.bucket,
    leads: row.leads,
    mqls: row.mqls,
    appointments: row.appointments,
    patients: row.patients,
    production: row.production,
    scheduledProduction: row.scheduledProduction,
    googleSpend: row.googleSpend,
    facebookSpend: row.facebookSpend,
    totalSpend: (row.googleSpend ?? 0) + (row.facebookSpend ?? 0),
    ROI: row.roi,
    budget: row.budget,
    channel: row.byChannel?.[0]?.channel,
  }
}

export function toDayRow(row: GqlRow): AttributionDayType {
  return {
    bucket: row.bucket,
    leads: row.leads,
    mqls: row.mqls,
    appointments: row.appointments,
    patients: row.patients,
    leadsByChannel: row.byChannel?.map((c) => ({ channel: c.channel, count: c.leads ?? 0 })) ?? [],
    mqlsByChannel: row.byChannel?.map((c) => ({ channel: c.channel, count: c.mqls ?? 0 })) ?? [],
    appointmentsByChannel: row.byChannel?.map((c) => ({ channel: c.channel, count: c.appointments ?? 0 })) ?? [],
    patientsByChannel: row.byChannel?.map((c) => ({ channel: c.channel, count: c.patients ?? 0 })) ?? [],
  }
}

/**
 * Ad Campaign rows as in the legacy Attribution route (trackable main): one row
 * per campaign name, which merges duplicate campaigns. The row keeps the id of
 * its first campaign for the row filter, sums the counts, production and spend,
 * computes ROI again, and is left out when it has no leads, appointments,
 * patients or production. A campaign that is not found keeps its id as the name.
 */
export function mergeAdCampaignRows(
  rows: AttributionRowType[],
  campaigns: { id: string; name: string }[],
): AttributionRowType[] {
  const names = new Map(campaigns.map((campaign) => [campaign.id, campaign.name]))
  const merged = new Map<string, AttributionRowType>()
  for (const row of rows) {
    const name = names.get(row.bucket) ?? row.bucket
    const current = merged.get(name)
    if (!current) {
      merged.set(name, { ...row, bucket: name, adCampaignId: row.bucket })
      continue
    }
    const add = (field: 'production' | 'scheduledProduction' | 'googleSpend' | 'facebookSpend' | 'budget') =>
      (current[field] ?? 0) + (row[field] ?? 0)
    merged.set(name, {
      ...current,
      leads: current.leads + row.leads,
      mqls: current.mqls + row.mqls,
      appointments: current.appointments + row.appointments,
      patients: current.patients + row.patients,
      production: add('production'),
      scheduledProduction: add('scheduledProduction'),
      googleSpend: add('googleSpend'),
      facebookSpend: add('facebookSpend'),
      budget: add('budget'),
    })
  }
  return [...merged.values()]
    .filter(
      (row) =>
        row.leads > 0 ||
        row.mqls > 0 ||
        row.appointments > 0 ||
        row.patients > 0 ||
        (row.production ?? 0) > 0 ||
        (row.scheduledProduction ?? 0) > 0,
    )
    .map((row) => {
      const totalSpend = (row.googleSpend ?? 0) + (row.facebookSpend ?? 0)
      const net = (row.production ?? 0) + (row.scheduledProduction ?? 0) - totalSpend
      // Same as calculateROI in the API: a whole percent, 0 without spend.
      const ROI = totalSpend ? Math.floor((Math.round((net / totalSpend) * 100) / 100) * 100) : 0
      return { ...row, totalSpend, ROI }
    })
    .sort((a, b) => b.leads - a.leads)
}

export function buildSummary(
  sourceRows: AttributionRowType[],
  mediumRows: AttributionRowType[],
  campaignRows: AttributionRowType[],
  summaryData?: { leads: number; mqls: number; appointments: number; patients: number },
): AttributionSummaryType | undefined {
  if (!summaryData) return undefined
  const topN = (rows: AttributionRowType[], field: keyof AttributionRowType) =>
    rows.map((r) => ({ name: r.bucket, count: (r[field] as number) ?? 0 })).filter((r) => r.count > 0)

  return {
    leads: {
      count: summaryData.leads,
      sources: topN(sourceRows, `leads`),
      mediums: topN(mediumRows, `leads`),
      campaigns: topN(campaignRows, `leads`),
    },
    mqls: {
      count: summaryData.mqls,
      sources: topN(sourceRows, `mqls`),
      mediums: topN(mediumRows, `mqls`),
      campaigns: topN(campaignRows, `mqls`),
    },
    appointments: {
      count: summaryData.appointments,
      sources: topN(sourceRows, `appointments`),
      mediums: topN(mediumRows, `appointments`),
      campaigns: topN(campaignRows, `appointments`),
    },
    patients: {
      count: summaryData.patients,
      sources: topN(sourceRows, `patients`),
      mediums: topN(mediumRows, `patients`),
      campaigns: topN(campaignRows, `patients`),
    },
  }
}

const GROUPINGS = [
  `utmSource`,
  `utmMedium`,
  `sourceMedium`,
  `utmCampaign`,
  `trackableCampaign`,
  `platform`,
  `channel`,
  `reportedAttribution`,
] as const

export default function useAttributionMetrics(
  fromDate: string,
  toDate: string,
  locationIds: string[],
  integrationStatus?: string,
  isV2Enabled?: boolean,
): [
  AttributionSummaryType | undefined,
  AttributionDayType[] | undefined,
  AttributionRowType[] | undefined,
  AttributionRowType[] | undefined,
  AttributionRowType[] | undefined,
  AttributionRowType[] | undefined,
  AttributionRowType[] | undefined,
  AttributionRowType[] | undefined,
  AttributionRowType[] | undefined,
  AttributionRowType[] | undefined,
  boolean,
] {
  const [summary, setSummary] = useState<AttributionSummaryType>()
  const [days, setDays] = useState<AttributionDayType[]>()
  const [sourceRows, setSourceRows] = useState<AttributionRowType[]>()
  const [mediumRows, setMediumRows] = useState<AttributionRowType[]>()
  const [sourceMediumRows, setSourceMediumRows] = useState<AttributionRowType[]>()
  const [campaignRows, setCampaignRows] = useState<AttributionRowType[]>()
  const [adCampaignRows, setAdCampaignRows] = useState<AttributionRowType[]>()
  const [adPlatformRows, setAdPlatformRows] = useState<AttributionRowType[]>()
  const [channelRows, setChannelRows] = useState<AttributionRowType[]>()
  const [reportedAttributionRows, setReportedAttributionRows] = useState<AttributionRowType[]>()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!fromDate || !toDate) return

    const dateRange = { fromDate, toDate }
    const baseVars = {
      dateRange,
      locationIds: locationIds.length ? locationIds : undefined,
      integrationStatus: integrationStatus || undefined,
      showDisqualified: isV2Enabled ? false : undefined,
    }

    setIsLoading(true)

    const dimensionQueries = GROUPINGS.map((groupBy) =>
      gqlQuery<GqlResponse>(ATTRIBUTION_METRICS_QUERY, { ...baseVars, groupBy }),
    )
    const daysQuery = gqlQuery<GqlResponse>(ATTRIBUTION_DAYS_QUERY, baseVars)

    Promise.all([...dimensionQueries, daysQuery])
      .then(
        async ([
          sourceRes,
          mediumRes,
          sourceMediumRes,
          campaignRes,
          adCampaignRes,
          adPlatformRes,
          channelRes,
          reportedRes,
          daysRes,
        ]) => {
          const src = sourceRes.data?.marketingMetrics.hits.map(toAttributionRow) ?? []
          const med = mediumRes.data?.marketingMetrics.hits.map(toAttributionRow) ?? []
          const srcMed = sourceMediumRes.data?.marketingMetrics.hits.map(toAttributionRow) ?? []
          const camp = campaignRes.data?.marketingMetrics.hits.map(toAttributionRow) ?? []
          const adCamp = adCampaignRes.data?.marketingMetrics.hits.map(toAttributionRow) ?? []
          const adPlat = adPlatformRes.data?.marketingMetrics.hits.map(toAttributionRow) ?? []
          const chan = channelRes.data?.marketingMetrics.hits.map(toAttributionRow) ?? []
          const reported = reportedRes.data?.marketingMetrics.hits.map(toAttributionRow) ?? []
          const dayRows = (daysRes.data?.marketingMetrics.hits.map(toDayRow) ?? []).sort((a, b) =>
            a.bucket.localeCompare(b.bucket),
          )
          const adCampaignIds = adCamp.map((row) => row.bucket)
          const adCampaigns = adCampaignIds.length
            ? await gqlQuery(ATTRIBUTION_AD_CAMPAIGNS_QUERY, {
                search: `id:(${adCampaignIds.map((id) => `"${id}"`).join(` OR `)})`,
                pagination: { page: 0, limit: adCampaignIds.length },
              })
            : undefined

          setSourceRows(src)
          setMediumRows(med)
          setSourceMediumRows(srcMed)
          setCampaignRows(camp)
          setAdCampaignRows(mergeAdCampaignRows(adCamp, adCampaigns?.data?.campaigns.hits ?? []))
          setAdPlatformRows(adPlat)
          setChannelRows(chan)
          setReportedAttributionRows(reported)
          setDays(dayRows)
          setSummary(buildSummary(src, med, camp, sourceRes.data?.marketingMetrics.summary))
        },
      )
      .finally(() => setIsLoading(false))
  }, [fromDate, toDate, locationIds, integrationStatus, isV2Enabled])

  return [
    summary,
    days,
    sourceRows,
    mediumRows,
    sourceMediumRows,
    campaignRows,
    adCampaignRows,
    adPlatformRows,
    channelRows,
    reportedAttributionRows,
    isLoading,
  ]
}
